import { Head, Link, router, usePoll } from "@inertiajs/react"
import {
  AlertCircle,
  ArrowLeft,
  Activity,
  Clock,
  Eye,
  Loader2,
  Video,
} from "lucide-react"
import type { ReactNode } from "react"
import { useCallback, useEffect, useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import MatchLayout from "@/layouts/match-layout"
import Timeline from "./timeline"

// ─── Types ─────────────────────────────────────────────────────────────────

interface MatchBrief {
  home_team: { name: string }
  away_team: { name: string }
  video_url: string | null
}

interface Observation {
  video_seconds: number
  video_time: string
  match_minute: number | null
  text: string
}

interface Report {
  id: number
  title: string
  status: "pending" | "generating" | "completed" | "failed"
  content: string
  observations: Observation[]
  created_at: string | null
  updated_at: string | null
}

interface MatchEvent {
  id: number
  minute: number
  event_type: string
  team: string
  confidence: string
  shirt_number: number | null
  detail: string | null
  player_name: string | null
}

interface MotionData {
  scores: { second: number; score: number }[]
  play_segments: number[][]
  total_play_seconds: number
  total_duration_seconds: number
}

interface MatchReportProps {
  match_id: string
  match: MatchBrief
  report: Report | null
  events: MatchEvent[]
  motion_data: MotionData
  can_analyze: boolean
}

// ─── YouTube helpers ───────────────────────────────────────────────────────

function getYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ]
  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match) return match[1]
  }
  return null
}

// ─── Video Player with seek + time tracking ────────────────────────────────

interface VideoPlayerProps {
  videoUrl: string
  onSeekReady: (seekFn: (seconds: number) => void) => void
  onTimeUpdate?: (seconds: number) => void
}

function VideoPlayer({ videoUrl, onSeekReady, onTimeUpdate }: VideoPlayerProps) {
  const youtubeId = getYouTubeId(videoUrl)
  const videoRef = useRef<HTMLVideoElement>(null)
  const playerRef = useRef<YTPlayer | null>(null)
  const timeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Poll current time every 250ms
  const startTimePolling = useCallback(() => {
    if (timeIntervalRef.current) clearInterval(timeIntervalRef.current)
    timeIntervalRef.current = setInterval(() => {
      if (youtubeId && playerRef.current) {
        const t = playerRef.current.getCurrentTime?.()
        if (typeof t === "number" && isFinite(t)) {
          onTimeUpdate?.(t)
        }
      } else if (videoRef.current) {
        onTimeUpdate?.(videoRef.current.currentTime)
      }
    }, 250)
  }, [youtubeId, onTimeUpdate])

  const onYouTubeReady = useCallback(
    (el: HTMLIFrameElement) => {
      if (!youtubeId) return

      if (!(window as any).YT) {
        const tag = document.createElement("script")
        tag.src = "https://www.youtube.com/iframe_api"
        document.head.appendChild(tag)
      }

      const initPlayer = () => {
        playerRef.current = new (window as any).YT.Player(el, {
          videoId: youtubeId,
          events: {
            onReady: () => {
              startTimePolling()
            },
          },
        })
      }

      if ((window as any).YT?.Player) {
        initPlayer()
      } else {
        ;(window as any).onYouTubeIframeAPIReady = initPlayer
      }
    },
    [youtubeId, startTimePolling]
  )

  onSeekReady((seconds: number) => {
    if (youtubeId && playerRef.current) {
      playerRef.current.seekTo(seconds, false)
    } else if (videoRef.current) {
      videoRef.current.currentTime = seconds
      videoRef.current.play()
    }
  })

  useEffect(() => {
    // For native video, use the timeupdate event instead of polling
    if (!youtubeId && videoRef.current) {
      const video = videoRef.current
      const handler = () => {
        onTimeUpdate?.(video.currentTime)
      }
      video.addEventListener("timeupdate", handler)
      startTimePolling()
      return () => {
        video.removeEventListener("timeupdate", handler)
        if (timeIntervalRef.current) clearInterval(timeIntervalRef.current)
      }
    }
    return () => {
      if (timeIntervalRef.current) clearInterval(timeIntervalRef.current)
    }
  }, [youtubeId, onTimeUpdate, startTimePolling])

  if (youtubeId) {
    return (
      <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
        <iframe
          ref={onYouTubeReady}
          className="absolute inset-0 h-full w-full rounded-lg"
          src={`https://www.youtube-nocookie.com/embed/${youtubeId}?enablejsapi=1`}
          title="Match video"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    )
  }

  return (
    <video
      ref={videoRef}
      className="w-full rounded-lg"
      controls
      preload="metadata"
    >
      <source src={videoUrl} />
      Your browser does not support the video tag.
    </video>
  )
}

// ─── Markdown Renderer ─────────────────────────────────────────────────────

function MarkdownContent({ content }: { content: string }) {
  const html = content
    .replace(
      /^### (.+)$/gm,
      '<h3 class="text-lg font-semibold mt-6 mb-2">$1</h3>'
    )
    .replace(
      /^## (.+)$/gm,
      '<h2 class="text-xl font-bold mt-8 mb-3 pb-2 border-b">$1</h2>'
    )
    .replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold mt-6 mb-3">$1</h1>')
    .replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>")
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold">$1</strong>')
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(
      /^- \[video [^\]]+\] (.+)$/gm,
      '<li class="ml-4 list-disc text-sm leading-relaxed">$1</li>'
    )
    .replace(
      /^- (.+)$/gm,
      '<li class="ml-4 list-disc text-sm leading-relaxed">$1</li>'
    )
    .replace(/^---$/gm, '<hr class="my-6 border-t">')
    .replace(
      /^_(.+)_$/gm,
      '<p class="text-xs text-muted-foreground italic mt-1">$1</p>'
    )
    .replace(
      /^(?!<[hluolhr])((?!<).+)$/gm,
      '<p class="text-sm leading-relaxed text-muted-foreground mt-2">$1</p>'
    )

  return (
    <div
      className="prose-sm max-w-none"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

// ─── Observations list ─────────────────────────────────────────────────────

function ObservationsPanel({
  observations,
  onSeek,
}: {
  observations: Observation[]
  onSeek: (seconds: number) => void
}) {
  if (observations.length === 0) return null

  return (
    <div className="bg-card rounded-lg border shadow-sm">
      <div className="border-b px-4 py-3">
        <h3 className="flex items-center gap-2 text-sm font-bold tracking-wider uppercase">
          <Clock className="h-4 w-4" />
          Observations ({observations.length})
        </h3>
      </div>
      <div className="max-h-96 divide-y overflow-y-auto">
        {observations.map((obs, i) => (
          <button
            key={i}
            onClick={() => onSeek(obs.video_seconds)}
            className="hover:bg-muted/50 flex w-full items-start gap-3 px-4 py-2.5 text-left transition-colors"
          >
            <span className="bg-primary/10 text-primary mt-0.5 inline-flex shrink-0 items-center gap-1 rounded px-2 py-0.5 font-mono text-xs font-medium">
              {obs.video_time}
              {obs.match_minute != null && (
                <span className="text-muted-foreground">
                  (~{obs.match_minute}')
                </span>
              )}
            </span>
            <span className="text-sm leading-relaxed">{obs.text}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

// ─── Status Components ─────────────────────────────────────────────────────

function PendingState({
  matchId,
  hasVideo,
  canAnalyze,
  hasMotionData,
}: {
  matchId: string
  hasVideo: boolean
  canAnalyze: boolean
  hasMotionData: boolean
}) {
  if (!hasVideo) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="bg-muted mb-4 flex h-16 w-16 items-center justify-center rounded-full">
          <Video className="text-muted-foreground h-8 w-8" />
        </div>
        <h2 className="text-lg font-semibold">No Video Available</h2>
        <p className="text-muted-foreground mt-1 max-w-md text-sm">
          Add a video URL to this match to run motion analysis and AI video analysis.
        </p>
      </div>
    )
  }

  if (!canAnalyze) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="bg-muted mb-4 flex h-16 w-16 items-center justify-center rounded-full">
          <Video className="text-muted-foreground h-8 w-8" />
        </div>
        <h2 className="text-lg font-semibold">Video Analysis</h2>
        <p className="text-muted-foreground mt-1 max-w-md text-sm">
          AI-powered video analysis is coming soon.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="bg-primary/10 mb-4 flex h-16 w-16 items-center justify-center rounded-full">
        <Eye className="text-primary h-8 w-8" />
      </div>
      <h2 className="text-lg font-semibold">Video Analysis</h2>
      <p className="text-muted-foreground mt-1 max-w-md text-sm">
        Generate a motion heatmap to see where play happens, then run full AI analysis.
      </p>
      <div className="mt-6 flex gap-3">
        <Button
          onClick={() =>
            router.post(`/matches/${matchId}/match_report/generate_motion`)
          }
          variant="outline"
          className="gap-2"
        >
          <Activity className="h-4 w-4" />
          {hasMotionData ? "Regenerate Motion Map" : "Generate Motion Map"}
        </Button>
        <Button
          onClick={() => router.post(`/matches/${matchId}/match_report`)}
          className="gap-2"
        >
          <Video className="h-4 w-4" />
          Full Analysis
        </Button>
      </div>
    </div>
  )
}

function GeneratingState({ matchId }: { matchId: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <Loader2 className="text-primary mb-4 h-10 w-10 animate-spin" />
      <h2 className="text-lg font-semibold">Analyzing Video…</h2>
      <div className="text-muted-foreground mt-2 max-w-md space-y-1 text-sm">
        <p>🤖 AI agent is navigating the match video frame by frame</p>
        <p>👀 Identifying events, scores, and play patterns</p>
        <p>📝 Recording observations as it goes</p>
      </div>
      <p className="text-muted-foreground mt-4 text-xs">
        This takes 5–10 minutes. Page refreshes automatically.
      </p>
      <Button
        variant="outline"
        onClick={() =>
          router.post(`/matches/${matchId}/match_report/cancel`)
        }
        className="text-destructive hover:bg-destructive mt-4 gap-2 hover:text-white"
      >
        Cancel Analysis
      </Button>
    </div>
  )
}

function FailedState({
  matchId,
  message,
  canAnalyze,
}: {
  matchId: string
  message: string
  canAnalyze: boolean
}) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <AlertCircle className="text-destructive mb-4 h-10 w-10" />
      <h2 className="text-destructive text-lg font-semibold">
        Analysis Failed
      </h2>
      <p className="text-muted-foreground mt-1 max-w-md text-sm">{message}</p>
      {canAnalyze && (
        <Button
          variant="outline"
          onClick={() => router.post(`/matches/${matchId}/match_report`)}
          className="mt-6 gap-2"
        >
          <Video className="h-4 w-4" />
          Try Again
        </Button>
      )}
    </div>
  )
}

// ─── Main Page ─────────────────────────────────────────────────────────────

export default function MatchReportPage({
  match_id,
  match,
  report,
  events,
  motion_data,
  can_analyze,
}: MatchReportProps) {
  const safeMatchId = match_id || ""
  const seekFnRef = useRef<((seconds: number) => void) | null>(null)
  const [currentTime, setCurrentTime] = useState(0)

  const hasVideo = !!match.video_url
  const hasMotionData =
    motion_data.scores.length > 0 && motion_data.total_duration_seconds > 0

  // Poll when report is loading, or report exists but motion data hasn't arrived
  const motionLoading = hasVideo && !hasMotionData && report != null
  const needsPoll =
    report?.status === "pending" ||
    report?.status === "generating" ||
    motionLoading

  usePoll(needsPoll ? 4000 : 0)

  const registerSeek = useCallback((fn: (seconds: number) => void) => {
    seekFnRef.current = fn
  }, [])

  const handleSeek = (seconds: number) => {
    seekFnRef.current?.(seconds)
  }

  const handleTimeUpdate = useCallback((seconds: number) => {
    setCurrentTime(seconds)
  }, [])

  const isAnalyzing =
    report?.status === "pending" || report?.status === "generating"

  return (
    <>
      <Head title="Video Analysis" />

      {/* Header */}
      <div className="bg-card border-b">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div>
            <div className="flex items-center gap-3">
              <Link
                href={`/matches/${safeMatchId}`}
                className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Link>
            </div>
            <h1 className="mt-1 text-xl font-bold tracking-tight">
              {report?.title ?? "Video Analysis"}
            </h1>
          </div>
          <div className="flex gap-2">
            {can_analyze && hasVideo && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    router.post(
                      `/matches/${safeMatchId}/match_report/generate_motion`
                    )
                  }
                  className="gap-1.5"
                >
                  <Activity className="h-3.5 w-3.5" />
                  Motion Map
                </Button>
                {!isAnalyzing && (
                  <Button
                    size="sm"
                    onClick={() =>
                      router.post(`/matches/${safeMatchId}/match_report`)
                    }
                    className="gap-1.5"
                  >
                    <Video className="h-3.5 w-3.5" />
                    Analyze
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-4">
        {/* ── No video state ── */}
        {!hasVideo && (
          <PendingState
            matchId={match_id}
            hasVideo={false}
            canAnalyze={can_analyze}
            hasMotionData={false}
          />
        )}

        {/* ── Has video: main layout ── */}
        {hasVideo && (
          <div className="space-y-3">
            {/* Video player */}
            <VideoPlayer
              videoUrl={match.video_url!}
              onSeekReady={registerSeek}
              onTimeUpdate={handleTimeUpdate}
            />

            {/* Motion loading */}
            {motionLoading && (
              <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-700">
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating motion heatmap…
              </div>
            )}

            {/* Timeline strip — directly under video */}
            {hasMotionData && (
              <Timeline
                motionData={motion_data}
                events={events}
                onSeek={handleSeek}
                currentTime={currentTime}
                homeTeamName={match.home_team.name}
                awayTeamName={match.away_team.name}
              />
            )}

            {/* Analyzing state */}
            {isAnalyzing && <GeneratingState matchId={match_id} />}

            {/* Failed state */}
            {report?.status === "failed" && (
              <FailedState
                matchId={match_id}
                message={report.content}
                canAnalyze={can_analyze}
              />
            )}

            {/* No report yet but has video */}
            {!report && !hasMotionData && (
              <PendingState
                matchId={match_id}
                hasVideo={true}
                canAnalyze={can_analyze}
                hasMotionData={false}
              />
            )}

            {/* Completed report content */}
            {report?.status === "completed" && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 rounded-md border border-yellow-300 bg-yellow-50 px-4 py-2 text-sm text-yellow-800">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  <strong>Draft</strong>
                  <span className="text-yellow-700">
                    — AI-generated. Review before sharing.
                  </span>
                </div>

                {report.observations.length > 0 && (
                  <ObservationsPanel
                    observations={report.observations}
                    onSeek={handleSeek}
                  />
                )}

                <div className="bg-card rounded-lg border p-6 shadow-sm">
                  <MarkdownContent content={report.content} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}

MatchReportPage.layout = (page: ReactNode) => (
  <MatchLayout>{page}</MatchLayout>
)
